import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Lightbulb, MapPin, Save, Search, Trash2, UserRound } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import Input, { Label, Select, Textarea } from "../../components/ui/Input";
import Skeleton from "../../components/ui/Skeleton";
import Tabs from "../../components/ui/Tabs";
import Pagination from "../../components/ui/Pagination";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";

/**
 * Feature requests sent from app pages inside the CRM.
 *
 * Triage happens here: read the request, move it through its status, and
 * write a reply. The reply and the status are what the account sees on its
 * app page, so both are written for them, not as internal notes.
 */

const STATUSES = [
  { id: "new", label: "New", tone: "neutral" },
  { id: "under_review", label: "Under review", tone: "amber" },
  { id: "planned", label: "Planned", tone: "violet" },
  { id: "in_progress", label: "In progress", tone: "brand" },
  { id: "done", label: "Shipped", tone: "emerald" },
  { id: "declined", label: "Not planned", tone: "rose" },
];
const statusOf = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

const ALL = "all";
const ALL_APPS = "";

const RequestItem = ({ request, open, onToggle, onSaved, onDeleted }) => {
  const { featureRequests } = useAppServices();
  const toast = useToast();
  const confirm = useConfirm();

  const [status, setStatus] = useState(request.status);
  const [response, setResponse] = useState(request.response || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // A save elsewhere (or a reload) replaces the row; follow it.
  useEffect(() => {
    setStatus(request.status);
    setResponse(request.response || "");
  }, [request.status, request.response]);

  const dirty = status !== request.status || response.trim() !== (request.response || "");
  const s = statusOf(request.status);
  const account = request.locationName || request.companyName || request.locationId || request.companyId;
  const who = request.submittedBy || {};

  const onSave = async () => {
    setSaving(true);
    const { response: updated, error } = await featureRequests.Update({
      _id: request._id,
      status,
      response,
    });
    setSaving(false);
    if (error) {
      toast(error?.message || "Could not save the request", "error");
      return;
    }
    toast("Request updated");
    onSaved(updated);
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: "Delete this request?",
      message: "It disappears from the account's app page too. This cannot be undone.",
      confirmLabel: "Delete request",
      tone: "danger",
    });
    if (!ok) return;
    setDeleting(true);
    const { error } = await featureRequests.Delete(request._id);
    setDeleting(false);
    if (error) {
      toast(error?.message || "Could not delete the request", "error");
      return;
    }
    toast("Request deleted");
    onDeleted(request._id);
  };

  return (
    <li className={cx("transition-colors", open ? "bg-ink-50/60" : "hover:bg-ink-50")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-5 py-3.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-900">{request.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-500">
            <span className="font-medium text-ink-700">{request.appName || request.appId}</span>
            <span className="text-ink-300">·</span>
            <span>{request.category}</span>
            {account && (
              <>
                <span className="text-ink-300">·</span>
                <span className="truncate">{account}</span>
              </>
            )}
          </p>
        </div>
        <span className="shrink-0 whitespace-nowrap pt-0.5 text-xs text-ink-400">
          {formatDate(request.createdAt)}
        </span>
        <Badge tone={s.tone} dot>
          {s.label}
        </Badge>
        <ChevronDown
          size={15}
          className={cx("mt-0.5 shrink-0 text-ink-400 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open && (
        <div className="grid gap-5 border-t border-ink-150 px-5 py-5 lg:grid-cols-[minmax(0,1fr)_17rem]">
          <div className="min-w-0 space-y-5">
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Request
              </p>
              <p className="whitespace-pre-line text-sm leading-relaxed text-ink-700">
                {request.description || "No details given."}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
              <div>
                <Label htmlFor={`st-${request._id}`}>Status</Label>
                <Select
                  id={`st-${request._id}`}
                  className="mt-1.5"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUSES.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor={`re-${request._id}`} hint="shown to the account">
                  Reply
                </Label>
                <Textarea
                  id={`re-${request._id}`}
                  className="mt-1.5"
                  rows={3}
                  maxLength={2000}
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Thanks — this is planned for the next release."
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="gradient"
                size="sm"
                loading={saving}
                disabled={!dirty}
                onClick={onSave}
                icon={!saving && <Save size={14} />}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!dirty || saving}
                onClick={() => {
                  setStatus(request.status);
                  setResponse(request.response || "");
                }}
              >
                Discard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                loading={deleting}
                onClick={onDelete}
                icon={!deleting && <Trash2 size={14} />}
              >
                Delete
              </Button>
            </div>
          </div>

          <dl className="space-y-3.5 rounded-xl border border-ink-200/70 bg-white p-4 text-[13px]">
            <div>
              <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <UserRound size={12} /> Sent by
              </dt>
              <dd className="mt-1 text-ink-800">{who.userName || "Unknown user"}</dd>
              {who.email && (
                <dd className="break-all text-ink-500">
                  <a href={`mailto:${who.email}`} className="hover:text-brand-600 hover:underline">
                    {who.email}
                  </a>
                </dd>
              )}
              {who.role && <dd className="text-xs text-ink-400">{who.role}</dd>}
            </div>
            <div>
              <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <MapPin size={12} /> Account
              </dt>
              <dd className="mt-1 text-ink-800">{account || "—"}</dd>
              <dd className="break-all font-mono text-[11px] text-ink-400">
                {request.locationId || request.companyId}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Dates</dt>
              <dd className="mt-1 text-ink-600">Sent {formatDate(request.createdAt)}</dd>
              {request.respondedAt && (
                <dd className="text-ink-600">Replied {formatDate(request.respondedAt)}</dd>
              )}
            </div>
          </dl>
        </div>
      )}
    </li>
  );
};

const CATEGORIES = ["New feature", "Improvement", "New integration"];
const SORTS = [
  { id: "newest", label: "Newest first" },
  { id: "oldest", label: "Oldest first" },
  { id: "updated", label: "Recently updated" },
];
const DEFAULTS = { page: 1, limit: 20, status: ALL, appId: ALL_APPS, category: "", sort: "newest", q: "" };

const FeatureRequests = () => {
  const { featureRequests, apps: appsService } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));

  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit, counts: {} });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [apps, setApps] = useState([]);

  // Paged and filtered on the server; the address holds the current view.
  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit, sort: query.sort });
    if (query.status !== ALL) params.set("status", query.status);
    if (query.appId) params.set("appId", query.appId);
    if (query.category) params.set("category", query.category);
    if (query.q) params.set("q", query.q);

    const { response, error } = await featureRequests.List(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load feature requests", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, counts: {} };
    // A page that emptied (last item deleted, or a narrower filter) steps back.
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.status, query.appId, query.category, query.sort, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    appsService.Get().then(({ response }) =>
      setApps(
        (Array.isArray(response) ? response : [])
          .filter((a) => a.appId)
          .map((a) => [a.appId, a.appName || a.appId])
          .sort((a, b) => a[1].localeCompare(b[1])),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = data.counts || {};
  const allCount = STATUSES.reduce((n, s) => n + (counts[s.id] || 0), 0);

  // Saved in place; the tab counts move with the status so they stay right
  // without reloading the page out from under the open editor.
  const replace = (updated) => {
    if (!updated?._id) return;
    setData((prev) => {
      const before = prev.items.find((r) => r._id === updated._id);
      const nextCounts = { ...prev.counts };
      if (before && before.status !== updated.status) {
        nextCounts[before.status] = Math.max(0, (nextCounts[before.status] || 0) - 1);
        nextCounts[updated.status] = (nextCounts[updated.status] || 0) + 1;
      }
      return { ...prev, counts: nextCounts, items: prev.items.map((r) => (r._id === updated._id ? updated : r)) };
    });
  };

  const remove = () => {
    setOpenId(null);
    load();
  };

  const filtered = Boolean(query.q || query.appId || query.category);
  const clearFilters = () => setQuery({ q: "", appId: ALL_APPS, category: "", status: ALL });

  const tabs = [
    { id: ALL, label: "All", badge: allCount },
    ...STATUSES.map((s) => ({ id: s.id, label: s.label, badge: counts[s.id] || 0 })),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[16rem] flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search requests, accounts or people..."
            className="pl-9"
            aria-label="Search feature requests"
          />
        </div>
        {/* The shared Select is always full width, so each wrapper sets it. */}
        <div className="w-full sm:w-52">
          <Select value={query.appId} onChange={(e) => setQuery({ appId: e.target.value })} aria-label="Filter by app">
            <option value={ALL_APPS}>All apps</option>
            {apps.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <Select value={query.category} onChange={(e) => setQuery({ category: e.target.value })} aria-label="Filter by type">
            <option value="">All types</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <Select value={query.sort} onChange={(e) => setQuery({ sort: e.target.value })} aria-label="Sort">
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Tabs tabs={tabs} active={query.status} onChange={(status) => setQuery({ status })} />

      <Card padded={false}>
        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<Lightbulb size={24} />}
            title={allCount === 0 && !filtered ? "No feature requests yet" : "Nothing matches"}
            description={
              allCount === 0 && !filtered
                ? "Requests sent from the Feature requests tab on any app page land here."
                : "Try another status, app, type or search term."
            }
            action={
              (filtered || query.status !== ALL) && (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              )
            }
          />
        ) : (
          <>
            <ul className="divide-y divide-ink-150">
              {data.items.map((r) => (
                <RequestItem
                  key={r._id}
                  request={r}
                  open={openId === r._id}
                  onToggle={() => setOpenId((id) => (id === r._id ? null : r._id))}
                  onSaved={replace}
                  onDeleted={remove}
                />
              ))}
            </ul>
            <Pagination
              page={data.page}
              pages={data.pages}
              total={data.total}
              limit={data.limit}
              noun={data.total === 1 ? "request" : "requests"}
              onPage={(page) => setQuery({ page })}
              onLimit={(limit) => setQuery({ limit })}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default FeatureRequests;
